/**
 * HexCanvas — a draw.io-style pan + zoom viewport (no external deps).
 *
 * Renders `children` inside one transformed layer (translate + scale, origin 0 0).
 * Drag empty space to pan. The wheel either zooms toward the cursor (`navigation="zoom"`,
 * the floor plan) or scrolls like a page with Ctrl/⌘ + wheel or a trackpad pinch zooming
 * (`navigation="scroll"`, the honeycomb). On touch devices one finger
 * pans and two fingers pinch-zoom toward the gesture midpoint. Drags starting on a hex
 * ([data-hex]) are ignored so clicks/taps work. Reports {tx,ty,scale} via
 * onViewportChange so the page can place screen-space overlays beside a hex
 * (screenX = tx + cx*scale, screenY = ty + cy*scale). fitToView()/resetView() via ref.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

export interface Viewport {
  tx: number;
  ty: number;
  scale: number;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
const MIN_VISIBLE = 140;
/** Scroll mode: how far past its own edge the content may be pulled — enough to read as an
 *  edge, not so much that the board can be lost. */
const EDGE = 16;
/** Wheel deltas at or above this (px) are a mouse notch, not a trackpad's continuous stream:
 *  those are glided toward rather than jumped to, which is what makes a notched wheel feel
 *  smooth. A trackpad already sends its own inertia, so it is applied as-is. */
const NOTCH_PX = 40;
/** Fraction of the remaining distance a glide covers per frame. */
const GLIDE = 0.28;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface HexCanvasProps {
  contentWidth: number;
  contentHeight: number;
  onViewportChange?: (vp: Viewport) => void;
  children: ReactNode;
  className?: string;
  /**
   * Consulted before a pan starts; returning false vetoes it.
   *
   * The `[data-hex]` hit test below is not enough on its own for children that drag. A
   * child's `pointerdown` handler cannot stop this `mousedown` — they are separate event
   * types, so `stopPropagation()` on one has no effect on the other, and the canvas would
   * pan underneath a marker the operator is dragging. Map View sets a ref in `pointerdown`
   * (which always precedes `mousedown`) and reports it here, which makes "dragging a pin"
   * and "panning the plan" mutually exclusive by construction rather than by geometry.
   */
  canPan?: () => boolean;
  /**
   * Hold the content STILL until it's zoomed in past its fitted size.
   *
   * A floor plan is a fixed backdrop — an operator placing pins on it expects the plan to
   * stay put under their cursor, not slide away, and at fit scale the whole thing is on
   * screen so there is nothing to pan toward anyway. Panning switches on only once zoom
   * pushes content outside the viewport, where it becomes necessary rather than surprising.
   * The hexagon board leaves this off: its canvas is unbounded, so panning is the point.
   */
  panWhenZoomedOnly?: boolean;
  /**
   * What "Fit" means on this canvas.
   *
   * 'contain' (default) puts the WHOLE content on screen — right for a floor plan, which is
   * a fixed image an operator wants to see all of.
   *
   * 'width' fits the content's width and lets it run off the bottom. The honeycomb is laid
   * out TO the canvas width, so containing it would scale the board down by its height until
   * it no longer reached either edge and then centre the shortfall — leaving wide empty
   * margins on both sides of a board that was built to fill them. Height is what the
   * operator scrolls; width is what they were given.
   */
  fitMode?: 'contain' | 'width';
  /**
   * Pin "Fit" to a fixed scale instead of measuring one.
   *
   * The honeycomb board wants a KNOWN starting zoom (the level operators actually work at)
   * on load and on every Fit press, not one that drifts with the viewport size. Given, this
   * replaces the `fitMode` measurement entirely; the content is still centred the same way.
   */
  fitScale?: number;
  /**
   * What the wheel does.
   *
   * 'zoom' (default) — the wheel zooms about the cursor. Right for a floor plan, which is a
   * picture the operator magnifies.
   *
   * 'scroll' — the canvas behaves like a page: the wheel / trackpad scrolls it, Shift + wheel
   * scrolls sideways, Ctrl/⌘ + wheel (which is also what a trackpad pinch sends) zooms, the
   * keyboard scrolls it once focused, a scrollbar shows where you are, and the content can't
   * be dragged off into empty space. The honeycomb is a long document of groups — making the
   * wheel zoom it meant every attempt to scroll down to the next group shrank the board
   * instead, and a trackpad's stream of tiny deltas zoomed it in lurches.
   */
  navigation?: 'zoom' | 'scroll';
}

export interface HexCanvasHandle {
  fitToView: () => void;
  resetView: () => void;
  /** Put the WHOLE content on screen, whatever `fitMode` / `fitScale` say "Fit" means. */
  fitAll: () => void;
  /** Zoom about the viewport centre — backs on-screen +/− controls. */
  zoomBy: (factor: number) => void;
  /** Glide so canvas-space `y` sits near the top of the viewport — "jump to group". */
  scrollToContentY: (y: number) => void;
}

const HexCanvas = forwardRef<HexCanvasHandle, HexCanvasProps>(function HexCanvas(
  {
    contentWidth,
    contentHeight,
    onViewportChange,
    children,
    className = '',
    canPan,
    panWhenZoomedOnly = false,
    fitMode = 'contain',
    fitScale,
    navigation = 'zoom',
  },
  ref,
) {
  // Read through a ref so the native touch listeners below (bound once) always see the
  // caller's current predicate instead of the one captured at mount.
  const canPanRef = useRef(canPan);
  canPanRef.current = canPan;
  const containerRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<Viewport>({ tx: 0, ty: 0, scale: 1 });
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const hasFitRef = useRef(false);
  const dragRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const interactedRef = useRef(false);
  const scrollMode = navigation === 'scroll';
  // Viewport size as STATE, only for the scrollbar's geometry (everything else reads the
  // element directly).
  const [viewSize, setViewSize] = useState({ w: 0, h: 0 });

  const clampTranslate = useCallback(
    (tx: number, ty: number, scale: number) => {
      const el = containerRef.current;
      if (!el) return { tx, ty };
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      const cw = contentWidth * scale;
      const ch = contentHeight * scale;
      if (scrollMode) {
        // Like a page: content that fits stays wholly on screen; content that doesn't can be
        // scrolled exactly to its ends (plus a small EDGE) and no further.
        const bound = (t: number, view: number, size: number) =>
          size <= view ? clamp(t, 0, view - size) : clamp(t, view - size - EDGE, EDGE);
        return { tx: bound(tx, vw, cw), ty: bound(ty, vh, ch) };
      }
      return {
        tx: clamp(tx, MIN_VISIBLE - cw, vw - MIN_VISIBLE),
        ty: clamp(ty, MIN_VISIBLE - ch, vh - MIN_VISIBLE),
      };
    },
    [contentWidth, contentHeight, scrollMode],
  );

  // ── Frame-coalesced viewport updates ──────────────────────────────────────
  // A wheel, trackpad or drag fires far more often than the screen repaints, and every
  // viewport change re-renders this canvas AND the page listening to onViewportChange. So
  // input writes the NEXT viewport into a ref and one requestAnimationFrame applies it —
  // at most one render per painted frame, however fast the events come. The same frame loop
  // drives glides (mouse-wheel notches, keyboard, scrollbar track, jump-to-group).
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<Viewport | null>(null);
  const glideRef = useRef<{ tx: number; ty: number } | null>(null);
  /** The viewport input should build on: whatever is queued, else what's on screen. */
  const latest = useCallback(() => pendingRef.current ?? vpRef.current, []);

  const flush = useCallback(() => {
    rafRef.current = null;
    const g = glideRef.current;
    if (g) {
      const c = pendingRef.current ?? vpRef.current;
      const dx = g.tx - c.tx;
      const dy = g.ty - c.ty;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        pendingRef.current = { ...c, tx: g.tx, ty: g.ty };
        glideRef.current = null;
      } else {
        pendingRef.current = { ...c, tx: c.tx + dx * GLIDE, ty: c.ty + dy * GLIDE };
      }
    }
    const next = pendingRef.current;
    if (next) {
      pendingRef.current = null;
      vpRef.current = next;
      setVp(next);
    }
    if (glideRef.current) rafRef.current = requestAnimationFrame(flush);
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  /** Queue a viewport for the next frame, cancelling any glide in flight. */
  const commit = useCallback(
    (next: Viewport) => {
      glideRef.current = null;
      pendingRef.current = next;
      schedule();
    },
    [schedule],
  );

  /** Apply a viewport NOW (no frame wait) — for fits, where a frame at the old scale flashes. */
  const applyNow = useCallback((next: Viewport) => {
    glideRef.current = null;
    pendingRef.current = null;
    vpRef.current = next;
    setVp(next);
  }, []);

  /** Glide the translation by (dx, dy) screen px, accumulating onto a glide already running. */
  const glideBy = useCallback(
    (dx: number, dy: number) => {
      const c = latest();
      const from = glideRef.current ?? c;
      glideRef.current = clampTranslate(from.tx + dx, from.ty + dy, c.scale);
      schedule();
    },
    [latest, clampTranslate, schedule],
  );

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  // The scale at which the whole content is on screen. `panWhenZoomedOnly` treats this as
  // the threshold: at or below it there is nothing off-screen to pan toward, so the content
  // is held still.
  const fitScaleRef = useRef(1);

  const fitToView = useCallback(() => {
    const el = containerRef.current;
    if (!el || contentWidth <= 0 || contentHeight <= 0) return;
    const vw = el.clientWidth;
    const vh = el.clientHeight;
    const scale = clamp(
      fitScale ??
        (fitMode === 'width'
          ? // Never PAST 1: a board with one group would otherwise be blown up to fill the
            // width, which is a magnifier, not a fit.
            Math.min(1, vw / contentWidth)
          : Math.min(vw / contentWidth, vh / contentHeight) * 0.96),
      MIN_SCALE,
      MAX_SCALE,
    );
    fitScaleRef.current = scale;
    const tx = (vw - contentWidth * scale) / 2;
    // Content taller than the viewport starts at the TOP — centring it would push the first
    // row off screen and hide the rest below, which is the opposite of what "Fit" promises.
    const ch = contentHeight * scale;
    const ty = ch < vh ? (vh - ch) / 2 : 0;
    applyNow({ tx, ty, scale });
  }, [contentWidth, contentHeight, fitMode, fitScale, applyNow]);

  const fitAll = useCallback(() => {
    const el = containerRef.current;
    if (!el || contentWidth <= 0 || contentHeight <= 0) return;
    interactedRef.current = true;
    const vw = el.clientWidth;
    const vh = el.clientHeight;
    // Never past 1 — "show all" on a small board is not a magnifier.
    const scale = clamp(Math.min(1, vw / contentWidth, vh / contentHeight) * 0.98, MIN_SCALE, MAX_SCALE);
    applyNow({ tx: (vw - contentWidth * scale) / 2, ty: (vh - contentHeight * scale) / 2, scale });
  }, [contentWidth, contentHeight, applyNow]);

  /** Is the content currently held still? Only meaningful with `panWhenZoomedOnly`. */
  const panLocked = useCallback(
    () => panWhenZoomedOnly && vpRef.current.scale <= fitScaleRef.current * 1.02,
    [panWhenZoomedOnly],
  );

  // Same read-through-a-ref reason as canPanRef: the touch listeners are bound once.
  const panLockedRef = useRef(panLocked);
  panLockedRef.current = panLocked;

  const zoomBy = useCallback(
    (factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      interactedRef.current = true;
      const cur = latest();
      const next = clamp(cur.scale * factor, MIN_SCALE, MAX_SCALE);
      if (next === cur.scale) return;
      // Zoom about the viewport centre, the same maths the wheel uses about the cursor.
      const mx = el.clientWidth / 2;
      const my = el.clientHeight / 2;
      const ntx = mx - (mx - cur.tx) * (next / cur.scale);
      const nty = my - (my - cur.ty) * (next / cur.scale);
      const c = clampTranslate(ntx, nty, next);
      commit({ tx: c.tx, ty: c.ty, scale: next });
    },
    [clampTranslate, latest, commit],
  );

  const scrollToContentY = useCallback(
    (y: number) => {
      interactedRef.current = true;
      const c = latest();
      glideRef.current = clampTranslate(c.tx, -y * c.scale + EDGE, c.scale);
      schedule();
    },
    [latest, clampTranslate, schedule],
  );

  useImperativeHandle(
    ref,
    () => ({ fitToView, resetView: fitToView, fitAll, zoomBy, scrollToContentY }),
    [fitToView, fitAll, zoomBy, scrollToContentY],
  );

  useLayoutEffect(() => {
    if (!hasFitRef.current && contentWidth > 0 && contentHeight > 0 && containerRef.current?.clientWidth) {
      fitToView();
      hasFitRef.current = true;
    }
  }, [contentWidth, contentHeight, fitToView]);

  useEffect(() => {
    onViewportChange?.(vp);
  }, [vp, onViewportChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setViewSize({ w: el.clientWidth, h: el.clientHeight });
      if (!interactedRef.current) fitToView();
      // A resize (or fullscreen toggle) can leave a scrolled board past its new end.
      else if (scrollMode) {
        const c = latest();
        const b = clampTranslate(c.tx, c.ty, c.scale);
        if (b.tx !== c.tx || b.ty !== c.ty) commit({ ...c, ...b });
      }
    });
    ro.observe(el);
    setViewSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [fitToView, scrollMode, latest, clampTranslate, commit]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      interactedRef.current = true;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      const dx = e.deltaX * unit;
      const dy = e.deltaY * unit;

      if (scrollMode && !e.ctrlKey && !e.metaKey) {
        // Shift + a vertical-only wheel scrolls sideways, as it does on a page.
        const [sx, sy] = e.shiftKey && !dx ? [dy, 0] : [dx, dy];
        if (Math.abs(sx) >= NOTCH_PX || Math.abs(sy) >= NOTCH_PX) {
          glideBy(-sx, -sy);
        } else {
          const c = latest();
          const b = clampTranslate(c.tx - sx, c.ty - sy, c.scale);
          // Keep a glide already in flight heading for the same place, shifted by this delta.
          if (glideRef.current) glideBy(-sx, -sy);
          else commit({ ...c, ...b });
        }
        return;
      }

      // Zoom, proportional to the delta: a mouse notch (~100px) is one ~12% step as before,
      // while a trackpad's stream of 2-4px deltas becomes a smooth, continuous zoom instead
      // of a 12% jump per event.
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cur = latest();
      const factor = clamp(Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0025)), 1 / 1.12, 1.12);
      const next = clamp(cur.scale * factor, MIN_SCALE, MAX_SCALE);
      if (next === cur.scale) return;
      const ntx = mx - (mx - cur.tx) * (next / cur.scale);
      const nty = my - (my - cur.ty) * (next / cur.scale);
      const c = clampTranslate(ntx, nty, next);
      commit({ tx: c.tx, ty: c.ty, scale: next });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [clampTranslate, scrollMode, latest, commit, glideBy]);

  // Touch: one finger pans, two fingers pinch-zoom toward the gesture midpoint.
  // Native listeners (passive: false on move) so we can preventDefault page scrolling
  // only while a gesture is actually panning/zooming — taps on hexes still click.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let pan: { x: number; y: number; tx: number; ty: number } | null = null;
    let pinch: { dist: number; cx: number; cy: number; scale: number; tx: number; ty: number } | null = null;

    const pt = (t: Touch) => {
      const r = el.getBoundingClientRect();
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const dist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        interactedRef.current = true;
        const [a, b] = [e.touches[0], e.touches[1]];
        const pa = pt(a);
        const pb = pt(b);
        pinch = {
          dist: dist(a, b),
          cx: (pa.x + pb.x) / 2,
          cy: (pa.y + pb.y) / 2,
          scale: latest().scale,
          tx: latest().tx,
          ty: latest().ty,
        };
        pan = null;
      } else if (e.touches.length === 1) {
        // A touch starting on a hex is a tap (click), not a pan — and one on the canvas's own
        // controls (the scrollbar) is theirs.
        if ((e.target as HTMLElement).closest('[data-hex],[data-canvas-ui]')) return;
        if (canPanRef.current && !canPanRef.current()) return;
        if (panLockedRef.current()) return;
        interactedRef.current = true;
        const p = pt(e.touches[0]);
        pan = { x: p.x, y: p.y, tx: latest().tx, ty: latest().ty };
        pinch = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (pinch && e.touches.length >= 2) {
        e.preventDefault();
        const [a, b] = [e.touches[0], e.touches[1]];
        const pa = pt(a);
        const pb = pt(b);
        const next = clamp(pinch.scale * (dist(a, b) / pinch.dist), MIN_SCALE, MAX_SCALE);
        const mx = (pa.x + pb.x) / 2;
        const my = (pa.y + pb.y) / 2;
        // Zoom toward the original midpoint, then follow the midpoint drift (pan).
        const ntx = pinch.cx - (pinch.cx - pinch.tx) * (next / pinch.scale) + (mx - pinch.cx);
        const nty = pinch.cy - (pinch.cy - pinch.ty) * (next / pinch.scale) + (my - pinch.cy);
        const c = clampTranslate(ntx, nty, next);
        commit({ tx: c.tx, ty: c.ty, scale: next });
      } else if (pan && e.touches.length === 1) {
        e.preventDefault();
        const p = pt(e.touches[0]);
        const scale = latest().scale;
        const c = clampTranslate(pan.tx + (p.x - pan.x), pan.ty + (p.y - pan.y), scale);
        commit({ tx: c.tx, ty: c.ty, scale });
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        pan = null;
        pinch = null;
      } else if (e.touches.length === 1) {
        // Pinch ended with one finger still down — continue as a pan from here.
        pinch = null;
        const p = pt(e.touches[0]);
        pan = { x: p.x, y: p.y, tx: latest().tx, ty: latest().ty };
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [clampTranslate, latest, commit]);

  const onMouseDown = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-hex],[data-canvas-ui]')) return;
    if (canPanRef.current && !canPanRef.current()) return;
    if (panLocked()) return;
    if (e.button !== 0) return;
    interactedRef.current = true;
    const cur = latest();
    dragRef.current = { startX: e.clientX, startY: e.clientY, tx: cur.tx, ty: cur.ty };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const scale = latest().scale;
      const c = clampTranslate(d.tx + (e.clientX - d.startX), d.ty + (e.clientY - d.startY), scale);
      commit({ tx: c.tx, ty: c.ty, scale });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, clampTranslate, latest, commit]);

  // ── Keyboard (scroll mode) — the board scrolls like a page once it has focus ──
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (!scrollMode || e.target !== e.currentTarget) return;
    const vh = containerRef.current?.clientHeight ?? 0;
    const page = Math.max(80, vh * 0.85);
    const steps: Record<string, number> = {
      ArrowDown: 80,
      ArrowUp: -80,
      PageDown: page,
      PageUp: -page,
      ' ': e.shiftKey ? -page : page,
      End: Number.MAX_SAFE_INTEGER / 4,
      Home: -Number.MAX_SAFE_INTEGER / 4,
    };
    const step = steps[e.key];
    if (step === undefined) return;
    e.preventDefault();
    interactedRef.current = true;
    glideBy(0, -step);
  };

  // ── Scrollbar (scroll mode) ───────────────────────────────────────────────
  // The canvas is a transform, not an overflow box, so the browser draws no scrollbar and
  // nothing told the operator the board went on below the fold — or how far. This one shows
  // both, and can be dragged or clicked like the real thing.
  const ch = contentHeight * vp.scale;
  const showBar = scrollMode && viewSize.h > 0 && ch > viewSize.h + 1;
  const trackH = Math.max(0, viewSize.h - 16);
  const range = ch - viewSize.h + EDGE * 2; // total ty travel: EDGE … vh − ch − EDGE
  const thumbH = showBar ? Math.max(36, trackH * (viewSize.h / (ch + EDGE * 2))) : 0;
  const progress = showBar ? clamp((EDGE - vp.ty) / range, 0, 1) : 0;
  const thumbTop = progress * (trackH - thumbH);
  const barDragRef = useRef<{ y: number; ty: number } | null>(null);

  const onThumbPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    interactedRef.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    barDragRef.current = { y: e.clientY, ty: latest().ty };
  };
  const onThumbPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = barDragRef.current;
    if (!d || trackH <= thumbH) return;
    const c = latest();
    const ty = d.ty - ((e.clientY - d.y) / (trackH - thumbH)) * range;
    commit({ ...c, ...clampTranslate(c.tx, ty, c.scale) });
  };
  const onThumbPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    barDragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };
  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    e.stopPropagation();
    interactedRef.current = true;
    const r = e.currentTarget.getBoundingClientRect();
    const above = e.clientY - r.top < thumbTop;
    glideBy(0, (above ? 1 : -1) * viewSize.h * 0.85);
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      // Focusable only where the keyboard does something (scroll mode). Clicking the canvas
      // focuses it, so the arrow keys work straight after the operator touches the board.
      tabIndex={scrollMode ? 0 : undefined}
      className={`relative h-full w-full touch-none overflow-hidden outline-none ${
        panWhenZoomedOnly && vp.scale <= fitScaleRef.current * 1.02
          ? 'cursor-default'
          : dragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
      } ${className}`}
    >
      <div
        className="absolute left-0 top-0 origin-top-left will-change-transform"
        style={{
          transform: `translate(${vp.tx}px, ${vp.ty}px) scale(${vp.scale})`,
          width: contentWidth,
          height: contentHeight,
        }}
      >
        {children}
      </div>

      {showBar && (
        <div
          data-canvas-ui
          aria-hidden
          onPointerDown={onTrackPointerDown}
          className="group/bar absolute bottom-2 right-1 top-2 z-30 w-2.5 cursor-default rounded-full transition-colors hover:bg-muted"
        >
          <div
            onPointerDown={onThumbPointerDown}
            onPointerMove={onThumbPointerMove}
            onPointerUp={onThumbPointerUp}
            onPointerCancel={onThumbPointerUp}
            className="absolute inset-x-0.5 rounded-full bg-muted-foreground opacity-40 transition-opacity group-hover/bar:opacity-70"
            style={{ top: thumbTop, height: thumbH }}
          />
        </div>
      )}
    </div>
  );
});

export default HexCanvas;
